import { loadingService, type LoaderType } from "./loading.service";

/**
 * Run async function with global loading indicator
 * Automatically shows loader at start and hides on completion/error
 *
 * @param asyncFn - Async function to execute
 * @param options - Options for loading behavior
 * @returns Promise with the result
 *
 * @example
 * ```typescript
 * // Central loader (default)
 * const result = await runAsync(async () => {
 *   return await apiService.getData();
 * });
 *
 * // Inline loader on button
 * const result = await runAsync(async () => {
 *   return await apiService.getData();
 * }, { loaderType: "inline", elementId: "submit-button" });
 * ```
 */
export const runAsync = async <T>(
  asyncFn: () => Promise<T>,
  options?: {
    showLoader?: boolean;
    loaderType?: LoaderType;
    elementId?: string;
    minLoadingTime?: number; // Minimum time to show loader (ms)
  }
): Promise<T> => {
  const showLoader = options?.showLoader !== false; // Default to true
  const loaderType = options?.loaderType || "central";
  const elementId = options?.elementId;
  const minLoadingTime = options?.minLoadingTime || 0;

  if (showLoader) {
    loadingService.show(loaderType, elementId);
  }

  const startTime = Date.now();

  try {
    const result = await asyncFn();

    // Ensure minimum loading time
    if (showLoader && minLoadingTime > 0) {
      const elapsed = Date.now() - startTime;
      const remaining = minLoadingTime - elapsed;
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    }

    return result;
  } finally {
    if (showLoader) {
      loadingService.hide();
    }
  }
};

/**
 * Run multiple async functions with single loading indicator
 *
 * @param asyncFns - Array of async functions to execute
 * @returns Promise with array of results
 *
 * @example
 * ```typescript
 * const [users, posts] = await runAsyncAll([
 *   () => userService.getUsers(),
 *   () => postService.getPosts(),
 * ]);
 * ```
 */
export const runAsyncAll = async <T>(
  asyncFns: Array<() => Promise<T>>,
  options?: {
    showLoader?: boolean;
    minLoadingTime?: number;
  }
): Promise<T[]> => {
  return runAsync(async () => {
    return Promise.all(asyncFns.map((fn) => fn()));
  }, options);
};
